import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ORDER_TYPE } from "../../services";
import { onlinePaymentService } from "../../services/onlinePayment";
import { settleLinkPayment } from "../../services/settlement";
import { brandingOf } from "../../config/branding";
import { onlinePaymentAvailable } from "../../config/online-payment";
import { useI18n } from "../../i18n";
import PaymentQrModal from "../../components/PaymentQrModal";
import BrandMark from "../../components/BrandMark";
import { Button, Card, Skeleton, baht, formatDate } from "../../components/ui";

// หน้าชำระเงินสาธารณะของลูกค้า — ไม่ต้องล็อกอิน เห็นเฉพาะร้าน/เลขเอกสาร/ยอด/สถานะ
export default function PayPage() {
  const { code } = useParams();
  const { tx } = useI18n();
  const [info, setInfo] = useState(undefined); // undefined = โหลด · null = ไม่พบ
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setInfo(await onlinePaymentService.lookup(code));
    } catch (err) {
      setError(err.message);
      setInfo(null);
    }
  }, [code]);

  useEffect(() => {
    load();
  }, [load]);

  if (info === undefined) return <Skeleton height={320} />;

  const notice = (title, text) => (
    <Card className="u-center" style={{ padding: 28 }}>
      <h2 style={{ fontSize: 20, fontWeight: 800 }}>{title}</h2>
      <div className="u-muted u-fs-13 u-mt-8">{text}</div>
    </Card>
  );

  if (!info) {
    return notice(tx("ไม่พบลิงก์ชำระเงิน", "Payment link not found"), error || tx("ตรวจสอบลิงก์อีกครั้ง หรือติดต่อร้านที่ส่งลิงก์ให้", "Check the link or contact the shop"));
  }

  const brand = brandingOf(info.shop);
  const shopName = brand.custom ? brand.name : info.shop?.name;
  const available = onlinePaymentAvailable(info.shop, info.settings);

  const header = (
    <div className="u-center u-mb-16">
      <BrandMark brand={brand} size={48} />
      <div className="u-strong u-fs-18 u-mt-8">{shopName}</div>
    </div>
  );

  let body;
  if (!available && info.status !== "paid") {
    body = notice(tx("ร้านยังไม่เปิดรับชำระออนไลน์", "This shop is not accepting online payments"), tx("กรุณาติดต่อร้านเพื่อชำระเงินช่องทางอื่น", "Please contact the shop to pay another way"));
  } else if (info.status === "paid") {
    body = (
      <Card className="u-center" style={{ padding: 28, background: "#ecfdf5", borderColor: "#6ee7b7" }}>
        <h2 style={{ fontSize: 20, fontWeight: 800 }}>{tx("ชำระเงินเรียบร้อยแล้ว", "Payment completed")}</h2>
        <div className="u-mt-8">{info.docNo} · {baht(info.amount)}</div>
        {info.settings?.thankYou ? <div className="u-mt-16" style={{ whiteSpace: "pre-wrap" }}>{info.settings.thankYou}</div> : null}
      </Card>
    );
  } else if (info.status === "expired") {
    body = notice(tx("ลิงก์นี้หมดอายุแล้ว", "This link has expired"), tx("กรุณาขอลิงก์ใหม่จากร้าน", "Please ask the shop for a new link"));
  } else if (info.status === "cancelled") {
    body = notice(tx("ลิงก์นี้ถูกยกเลิกแล้ว", "This link was cancelled"), tx("กรุณาติดต่อร้าน", "Please contact the shop"));
  } else {
    body = (
      <Card className="u-center" style={{ padding: 28 }}>
        <div className="u-fs-13 u-muted">{info.title || info.docNo}</div>
        {info.docNo ? <div className="u-strong">{info.docNo}</div> : null}
        <div style={{ fontSize: 34, fontWeight: 900, margin: "12px 0" }}>{baht(info.amount)}</div>
        {info.expireAt ? <div className="u-fs-12 u-muted u-mb-8">{tx("ชำระได้ถึง", "Pay before")} {formatDate(info.expireAt)}</div> : null}
        <Button variant="primary" block onClick={() => setOpen(true)}>{tx("ชำระเงินด้วย QR", "Pay with QR")}</Button>
      </Card>
    );
  }

  return (
    <>
      {header}
      {body}
      <PaymentQrModal
        open={open}
        title={info.title || info.docNo}
        subtitle={shopName}
        amount={info.amount}
        orderType={ORDER_TYPE.paymentLink}
        reference={info.docNo || info.code}
        orgId={info.orgId}
        items={[{ productName: info.docNo || info.title || info.code, quantity: 1, price: info.amount }]}
        onCreated={(qr) => onlinePaymentService.markPending(info.orgId, info.linkId, qr)}
        onClose={() => {
          setOpen(false);
          load();
        }}
        onPaid={async (qr) => {
          await settleLinkPayment({
            orgId: info.orgId,
            link: { id: info.linkId, invoiceId: info.invoiceId, amount: info.amount, title: info.title, code: info.code },
            transactionId: qr.transactionId,
            amount: qr.amount,
            refNo: qr.refNo,
          });
          load();
        }}
      />
    </>
  );
}
